'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Bell } from 'lucide-react';
import { useAuth } from '@/lib/context/User.context';

interface NotificationBadgeProps {
  className?: string;
}

export default function NotificationBadge({ className = '' }: NotificationBadgeProps) {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userProfile } = useAuth();

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!userProfile?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`/api/notifications/count?userId=${userProfile.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.unreadCount || 0);
        } else {
          console.error('Failed to fetch notification count:', response.status);
          setUnreadCount(0);
        }
      } catch (error) {
        console.error('Error fetching notification count:', error);
        setUnreadCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnreadCount();

    // Set up polling to refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    // Listen for focus events to refresh when user returns to tab
    const handleFocus = () => {
      fetchUnreadCount();
    };

    // Listen for custom events when notifications are marked as read
    const handleNotificationUpdate = () => {
      fetchUnreadCount();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('notificationsUpdated', handleNotificationUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('notificationsUpdated', handleNotificationUpdate);
    };
  }, [userProfile?.id]);

  return (
    <Link data-component-id="src/components/common/NotificationBadge.tsx:70:4" data-component-path="src/components/common/NotificationBadge.tsx" data-component-line="70" data-component-file="NotificationBadge.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fnotifications%22%7D"
      href="/notifications"
      className={`relative inline-flex items-center justify-center p-2 rounded-full hover:bg-[var(--bg-card)]/50 transition-colors ${className}`}
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Bell data-component-id="src/components/common/NotificationBadge.tsx:75:6" data-component-path="src/components/common/NotificationBadge.tsx" data-component-line="75" data-component-file="NotificationBadge.tsx" data-component-name="Bell" data-component-content="%7B%22elementName%22%3A%22Bell%22%2C%22className%22%3A%22w-5%20h-5%20text-%5Bvar(--text-primary)%5D%22%7D" className="w-5 h-5 text-[var(--text-primary)]" />
      
      {/* Notification Badge */}
      {!isLoading && unreadCount > 0 && (
        <span data-component-id="src/components/common/NotificationBadge.tsx:79:8" data-component-path="src/components/common/NotificationBadge.tsx" data-component-line="79" data-component-file="NotificationBadge.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20-top-1%20-right-1%20min-w-%5B18px%5D%20h-%5B18px%5D%20bg-red-500%20text-%5B%23FFFFFF%5D%20text-xs%20font-medium%20rounded-full%20flex%20items-center%20justify-center%20px-1%22%7D" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-[#FFFFFF] text-xs font-medium rounded-full flex items-center justify-center px-1">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <span data-component-id="src/components/common/NotificationBadge.tsx:86:8" data-component-path="src/components/common/NotificationBadge.tsx" data-component-line="86" data-component-file="NotificationBadge.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20-top-1%20-right-1%20w-2%20h-2%20bg-gray-400%20rounded-full%22%7D" className="absolute -top-1 -right-1 w-2 h-2 bg-gray-400 rounded-full" />
      )}
    </Link>
  );
}
